import React, { useState } from "react";
import { api } from "@/lib/api";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import {
  ShieldAlert, Sparkles, Loader2, RefreshCw, WifiOff, CheckCircle2,
  Clock, History, MapPin, Bell, ChevronDown, ChevronRight,
} from "lucide-react";
import { styleFor } from "@/lib/incidents";

const RISK = {
  red: { c: "bg-rose-100 text-rose-700 border-rose-200", t: "Disruption" },
  amber: { c: "bg-amber-100 text-amber-700 border-amber-200", t: "Possible delay" },
  green: { c: "bg-emerald-100 text-emerald-700 border-emerald-200", t: "Clear" },
  grey: { c: "bg-slate-100 text-slate-500 border-slate-200", t: "No data" },
};

const fmtWhen = (iso) => {
  if (!iso) return "";
  try {
    return new Date(iso).toLocaleString("en-GB", { day: "numeric", month: "short", hour: "2-digit", minute: "2-digit" });
  } catch { return ""; }
};
const dayLabel = (iso) => {
  if (!iso) return "";
  try { return new Date(iso + "T00:00:00").toLocaleDateString("en-GB", { weekday: "short", day: "numeric", month: "short" }); }
  catch { return iso; }
};

// Live disruption overview for a saved run: rule-based warnings, per-day suggested
// departures, AI pre-departure briefing, and incident history / recurring hotspots.
// Laid out as two columns on wide screens: main (status + departures + why + briefing)
// on the left, activity (alerts + history/hotspots) on the right.
export default function RunDisruptionPanel({ runId, data, busy, initialBriefing, alerts, onRefresh }) {
  const [brief, setBrief] = useState(initialBriefing || null);
  const [bBusy, setBBusy] = useState(false);
  const [showWhy, setShowWhy] = useState(false);

  const genBrief = async () => {
    setBBusy(true);
    try { const r = await api.post(`/runs/${runId}/disruption-briefing`); setBrief(r.data); }
    catch { toast.error("Could not generate the briefing"); }
    finally { setBBusy(false); }
  };

  const scan = data && data.scan;
  const days = scan?.days || [];
  const affecting = scan?.overall?.count_affecting || 0;
  const feedDown = days.length > 0 && days.every((d) => d.enabled === false);
  const departures = days.filter((d) => d.suggested_departure);
  const history = data?.history || [];
  const hotspots = data?.hotspots || [];
  const recentAlerts = [...(alerts || [])].reverse().slice(0, 4);
  const rk = RISK[scan?.risk] || RISK.grey;
  const bd = scan?.breakdown;

  const statusBlock = affecting > 0 ? (
    <div className="rounded-xl border border-amber-300 bg-amber-50 px-3.5 py-2.5 text-sm text-amber-900 font-semibold" data-testid="disruption-summary">
      ⚠ {affecting} incident{affecting > 1 ? "s" : ""} affecting this run across {days.length} day{days.length > 1 ? "s" : ""}
    </div>
  ) : (
    <div className="flex items-center gap-2 text-sm text-emerald-600" data-testid="disruption-clear">
      <CheckCircle2 className="w-4 h-4" /> No live incidents currently affecting this run.
    </div>
  );

  const departuresBlock = departures.length > 0 && (
    <div data-testid="disruption-departures">
      <div className="text-xs font-semibold uppercase tracking-wide text-slate-400 mb-1.5 flex items-center gap-1.5">
        <Clock className="w-3.5 h-3.5" /> Suggested departure adjustments
      </div>
      <ul className="space-y-1">
        {departures.map((d) => (
          <li key={d.day_index} className="text-sm text-slate-700">
            <span className="font-medium">{dayLabel(d.date)}</span>
            {d.driver ? ` · ${d.driver}` : ""} — leave by <b className="text-amber-700">{d.suggested_departure}</b>
            <span className="text-slate-400"> (planned {d.start_time}, +{d.summary?.delay_mins || 0} min traffic{d.summary?.event_buffer_mins ? ` +${d.summary.event_buffer_mins} min event buffer` : ""})</span>
          </li>
        ))}
      </ul>
    </div>
  );

  const whyBlock = bd && bd.components && bd.components.length > 0 && (
    <div data-testid="why-this-risk">
      <button onClick={() => setShowWhy((v) => !v)} className="inline-flex items-center gap-1 text-xs font-semibold text-slate-500 hover:text-slate-700" data-testid="why-this-risk-toggle">
        {showWhy ? <ChevronDown className="w-3.5 h-3.5" /> : <ChevronRight className="w-3.5 h-3.5" />} Why this risk?
      </button>
      {showWhy && (
        <div className="mt-2 rounded-xl border border-slate-200 bg-slate-50 p-3" data-testid="why-this-risk-body">
          <div className="flex items-center justify-between text-[11px] text-slate-400 mb-2">
            <span className={`inline-flex items-center gap-1 text-[11px] font-semibold rounded-full border px-2 py-0.5 ${rk.c}`}>
              <span className="w-1.5 h-1.5 rounded-full bg-current opacity-70" /> {rk.t}
            </span>
            <span>Last checked {fmtWhen(bd.last_checked)}</span>
          </div>
          <ul className="space-y-1.5">
            {bd.components.map((c, i) => (
              <li key={i} className="text-[13px] text-slate-700 flex items-start gap-2" data-testid={`why-component-${i}`}>
                <span className="text-base leading-5">{styleFor(c.icon_kind).emoji}</span>
                <span>
                  <b>{c.label}</b> — {c.detail}
                  {c.basis === "measured" && <span className="text-rose-700"> · +{c.amount_mins} min measured delay</span>}
                  {c.basis === "precautionary" && <span className="text-violet-700"> · +{c.amount_mins} min precautionary buffer</span>}
                  {c.basis === "advisory" && <span className="text-sky-700"> · advisory (no added delay)</span>}
                  {c.basis === "none" && <span className="text-emerald-600"> · no current impact</span>}
                  {c.basis === "unavailable" && <span className="text-slate-400"> · data unavailable</span>}
                  <span className="text-slate-400"> ({c.source})</span>
                </span>
              </li>
            ))}
          </ul>
          <div className="mt-2 pt-2 border-t border-slate-200 text-[13px] text-slate-700" data-testid="why-derivation">
            Recommended departure adjustment: <b className="text-amber-700">+{bd.total_adjustment_mins} min</b>
            <span className="text-slate-400"> = traffic +{bd.traffic_delay_mins} min + event buffer +{bd.event_buffer_mins} min{bd.focus_date ? ` · for ${dayLabel(bd.focus_date)}` : ""}</span>
            {bd.multi_day && <div className="text-[11px] text-slate-400 mt-0.5">Other days on this run also have disruption — see suggested departures above.</div>}
          </div>
        </div>
      )}
    </div>
  );

  const briefingBlock = (
    <div className="pt-4 border-t border-slate-100" data-testid="disruption-briefing">
      <div className="flex items-center justify-between gap-3 mb-2">
        <div className="text-xs font-semibold uppercase tracking-wide text-slate-400 flex items-center gap-1.5">
          <Sparkles className="w-3.5 h-3.5" /> AI pre-departure briefing
        </div>
        <Button size="sm" onClick={genBrief} disabled={bBusy} className="gap-1.5 bg-indigo-600 hover:bg-indigo-700 text-white" data-testid="disruption-briefing-btn">
          {bBusy ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Sparkles className="w-3.5 h-3.5" />}
          {brief ? "Regenerate" : "Generate briefing"}
        </Button>
      </div>
      {brief ? (
        <div data-testid="disruption-briefing-text">
          <p className="text-sm text-slate-700 whitespace-pre-line leading-relaxed">{brief.text}</p>
          {brief.generated_at && <p className="text-[11px] text-slate-400 mt-1">Generated {fmtWhen(brief.generated_at)}</p>}
        </div>
      ) : (
        <p className="text-sm text-slate-400">Generate a short, engineer-ready summary of the delays to expect before leaving.</p>
      )}
    </div>
  );

  const alertsBlock = recentAlerts.length > 0 && (
    <div className="space-y-1.5" data-testid="disruption-alerts">
      <div className="text-xs font-semibold uppercase tracking-wide text-slate-400 mb-0.5 flex items-center gap-1.5">
        <Bell className="w-3.5 h-3.5" /> Recent alerts
      </div>
      {recentAlerts.map((a, i) => (
        <div key={i} className="rounded-lg bg-indigo-50 border border-indigo-100 px-3 py-2 flex items-start gap-2" data-testid={`disruption-alert-${i}`}>
          <Bell className="w-3.5 h-3.5 text-indigo-500 mt-0.5 shrink-0" />
          <div className="text-[13px] text-slate-700">
            <span className="font-semibold text-indigo-800">{a.headline}</span> — {a.text}
            <span className="text-slate-400"> · {fmtWhen(a.at)}</span>
          </div>
        </div>
      ))}
    </div>
  );

  const historyBlock = (history.length > 0 || hotspots.length > 0) && (
    <div data-testid="disruption-history">
      <div className="text-xs font-semibold uppercase tracking-wide text-slate-400 mb-2 flex items-center gap-1.5">
        <History className="w-3.5 h-3.5" /> Incident history &amp; hotspots
      </div>
      {hotspots.length > 0 && (
        <div className="flex flex-wrap gap-1.5 mb-2" data-testid="disruption-hotspots">
          {hotspots.map((h) => (
            <span key={h.road} className="inline-flex items-center gap-1 text-[11px] font-medium bg-rose-50 text-rose-700 border border-rose-200 rounded-full px-2 py-0.5">
              <MapPin className="w-3 h-3" /> {h.road} · {h.occurrences}×
            </span>
          ))}
        </div>
      )}
      <ul className="space-y-0.5">
        {[...history].reverse().slice(0, 6).map((h, i) => {
          const tot = (h.days || []).reduce((a, d) => a + (d.count_affecting || 0), 0);
          return (
            <li key={i} className="text-[12px] text-slate-500 flex items-center justify-between">
              <span>{fmtWhen(h.at)}</span>
              <span className={tot > 0 ? "text-amber-700" : "text-slate-400"}>{tot} affecting</span>
            </li>
          );
        })}
      </ul>
    </div>
  );

  const hasSide = recentAlerts.length > 0 || history.length > 0 || hotspots.length > 0;

  return (
    <div className="mb-5 rounded-2xl border border-slate-200 bg-white shadow-sm p-5" data-testid="run-disruption-panel">
      <div className="flex items-center justify-between gap-3 mb-4">
        <h2 className="text-base font-bold text-slate-900 flex items-center gap-2">
          <ShieldAlert className="w-5 h-5 text-indigo-500" /> Live disruption
          {scan && (
            <span className={`inline-flex items-center gap-1 text-[11px] font-semibold rounded-full border px-2 py-0.5 ${rk.c}`} data-testid="disruption-risk-chip">
              <span className="w-1.5 h-1.5 rounded-full bg-current opacity-70" /> {rk.t}
            </span>
          )}
        </h2>
        <Button variant="outline" size="sm" onClick={onRefresh} disabled={busy} className="gap-1.5" data-testid="disruption-refresh-btn">
          {busy ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <RefreshCw className="w-3.5 h-3.5" />} Refresh
        </Button>
      </div>

      {busy && !data && (
        <div className="flex items-center gap-2 text-sm text-slate-400" data-testid="disruption-loading">
          <Loader2 className="w-4 h-4 animate-spin" /> Scanning the route for live traffic incidents…
        </div>
      )}

      {!busy && (data === false) && (
        <div className="flex items-center gap-2 text-sm text-slate-400" data-testid="disruption-error">
          <WifiOff className="w-4 h-4" /> Traffic incident data temporarily unavailable. Google routing &amp; ETAs are unaffected.
        </div>
      )}

      {scan && (feedDown ? (
        <div className="flex items-center gap-2 text-sm text-slate-400" data-testid="disruption-feed-down">
          <WifiOff className="w-4 h-4" /> Traffic incident data temporarily unavailable. Google routing &amp; ETAs are unaffected.
        </div>
      ) : (
        <div className={`grid grid-cols-1 ${hasSide ? "lg:grid-cols-2" : ""} gap-x-8 gap-y-5`} data-testid="disruption-grid">
          <div className="space-y-4 min-w-0" data-testid="disruption-col-main">
            {statusBlock}
            {departuresBlock}
            {whyBlock}
            {briefingBlock}
          </div>
          {hasSide && (
            <div className="space-y-5 min-w-0 lg:border-l lg:border-slate-100 lg:pl-8" data-testid="disruption-col-side">
              {alertsBlock}
              {historyBlock}
            </div>
          )}
        </div>
      ))}
    </div>
  );
}
